import { useEffect, useMemo, useRef, useState } from "react";
import TopBanner from "../../shared/components/TopBanner";
import { players as legacyBasePlayersArr } from "../../shared/data/rankings";
import ktcPlayerIdsJson from "../../shared/data/ktcPlayerIds.json";
import rankingsCsvUrl from "../../shared/data/rankings.csv";
import type { Player } from "../../shared/models/Player";
import { usePlayers } from "../../shared/hooks/usePlayers";
import { useSleeperDirectory } from "../../shared/hooks/useSleeperDirectory";
import { normalizeName } from "../../shared/utils/espn";
import { posColor } from "../../shared/utils/posColor";
import { useDynastyFindTrade } from "../hooks/useDynastyFindTrade";
import { useDynastyLeagueImport } from "../hooks/useDynastyLeagueImport";
import { useDynastyMobileUi } from "../hooks/useDynastyMobileUi";
import { useDynastyTradeWorkspace } from "../hooks/useDynastyTradeWorkspace";
import { useDynastyViewport } from "../hooks/useDynastyViewport";
import { calculateKtcStyleAdjustedTotals } from "../utils/dynastyTradeCalculator";
import { importRankingsCsv } from "../../shared/utils/xlsxRankings";
import {
  DynastyLeagueImportCard,
  ImportedTeamsLayout,
  MobileTradeMeterOverlay,
  PlayerLookupPanel,
  TeamAvatar,
  TeamTradeHistoryPanel,
  TradeAssetCard,
} from "../features/shared/dynastyTradingComponents";
import {
  formatValue,
  getPositionColor,
  importPickPlayersFromCsv,
  isTradeablePlayerAsset,
} from "../features/shared/dynastyTradingPageUtils";

const noop = () => {};
const LOCAL_KTC_CSV_URL = "/data/ktc-rankings.csv";
const DYNASTY_COLUMN_HEIGHT = "calc(100vh - var(--top-links-bar-height) - 80px)";
const DYNASTY_ROSTER_COLUMN_WIDTH = 330;
const DYNASTY_SETUP_COLUMN_WIDTH = 690;

type FindTradeTargetPosition = "QB" | "RB" | "WR" | "TE" | "";

type DynastyTradingPageProps = {
  fixedLeagueIdentifier?: string;
  hideFindTrade?: boolean;
  hideLeagueSummary?: boolean;
  hideTopBanner?: boolean;
  hideTradeCalculations?: boolean;
  showOpenInKtc?: boolean;
  fixedLeagueLoadingImageSrc?: string;
  theme?: "default" | "leagueOfLegends";
  loadingTitle?: string;
  loadingSubtitle?: string;
  persistLeagueIdentifier?: boolean;
};

const ktcPlayerIdsByName = ktcPlayerIdsJson as Record<string, string>;

function SlotLabel({ slot }: { slot: string }) {
  if (slot === "FLEX") {
    return (
      <>
        <span style={{ color: posColor("WR") }}>W</span>
        <span style={{ color: posColor("RB") }}>R</span>
        <span style={{ color: posColor("TE") }}>T</span>
      </>
    );
  }

  if (slot === "SF") {
    return (
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          lineHeight: 0.9,
        }}
      >
        <span>
          <span style={{ color: posColor("QB") }}>Q</span>
          <span style={{ color: posColor("WR") }}>W</span>
        </span>
        <span>
          <span style={{ color: posColor("RB") }}>R</span>
          <span style={{ color: posColor("TE") }}>T</span>
        </span>
      </span>
    );
  }

  return <>{slot}</>;
}

function DynastyAutoLeagueStatusCard(props: {
  imageSrc?: string;
  importProgress: number;
  isImportingLeague: boolean;
  isNarrowViewport: boolean;
  leagueError: string;
  subtitle: string;
  title: string;
}) {
  const { imageSrc, importProgress, isImportingLeague, isNarrowViewport, leagueError, subtitle, title } = props;
  const [imageFailed, setImageFailed] = useState(false);
  const progress = leagueError
    ? 100
    : Math.max(isImportingLeague ? 8 : 0, Math.min(100, Math.round(importProgress)));

  return (
    <section
      style={{
        minHeight: "calc(100vh - var(--top-links-bar-height, 0px) - 40px)",
        display: "grid",
        placeItems: "center",
        padding: isNarrowViewport ? "24px 12px" : "40px 24px",
        background: "#000",
      }}
    >
      <div
        style={{
          width: "min(920px, 94vw)",
          display: "grid",
          gap: 18,
          justifyItems: "center",
        }}
      >
        {imageSrc && !imageFailed ? (
          <img
            src={imageSrc}
            alt={title}
            onError={() => setImageFailed(true)}
            style={{
              width: "min(760px, 92vw)",
              maxHeight: isNarrowViewport ? "44vh" : "58vh",
              objectFit: "contain",
              borderRadius: 14,
              boxShadow: "0 26px 80px rgba(0,0,0,0.42)",
            }}
          />
        ) : (
          <div
            style={{
              width: "min(760px, 92vw)",
              aspectRatio: "16 / 9",
              borderRadius: 14,
              display: "grid",
              placeItems: "center",
              background:
                "radial-gradient(circle at 50% 42%, rgba(250,204,21,0.16), rgba(15,23,42,0.96) 54%, rgba(2,6,23,1))",
              border: "1px solid rgba(250,204,21,0.18)",
              boxShadow: "0 26px 80px rgba(0,0,0,0.42)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                fontSize: isNarrowViewport ? 34 : 58,
                lineHeight: 0.95,
                fontWeight: 950,
                color: "#f8d36a",
                textAlign: "center",
                textShadow: "0 4px 22px rgba(0,0,0,0.8)",
              }}
            >
              League of<br />Legends
            </div>
          </div>
        )}

        <div
          style={{
            width: "min(520px, 76vw)",
            display: "grid",
            gap: 8,
          }}
        >
          <div
            style={{
              height: 10,
              borderRadius: 999,
              border: "1px solid rgba(250,204,21,0.28)",
              background: "rgba(255,255,255,0.08)",
              overflow: "hidden",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                borderRadius: 999,
                background: leagueError
                  ? "linear-gradient(90deg, #ef4444, #f97316)"
                  : "linear-gradient(90deg, #b7791f, #facc15, #fef3c7)",
                transition: "width 420ms ease",
              }}
            />
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: leagueError ? "rgb(248,113,113)" : "rgba(255,255,255,0.66)",
              textAlign: "center",
            }}
          >
            {leagueError || (isImportingLeague ? "Loading Sleeper league..." : subtitle)}
          </div>
        </div>
      </div>
    </section>
  );
}

function buildKtcTradeCalculatorUrl(args: {
  format: "1QB" | "2QB";
  leftIncomingAssets: Array<{ name: string; position?: string }>;
  rightIncomingAssets: Array<{ name: string; position?: string }>;
}) {
  const getKtcIds = (assets: Array<{ name: string; position?: string }>) =>
    assets
      .filter((asset) => String(asset.position ?? "").toUpperCase() !== "PI")
      .map((asset) => ktcPlayerIdsByName[normalizeName(asset.name)])
      .filter((id): id is string => Boolean(id));

  const leftIds = getKtcIds(args.leftIncomingAssets);
  const rightIds = getKtcIds(args.rightIncomingAssets);
  if (!leftIds.length && !rightIds.length) return "";

  const params = new URLSearchParams({
    format: args.format === "1QB" ? "1" : "2",
    isStartup: "0",
    tep: "0",
  });
  if (leftIds.length) params.set("teamOne", leftIds.join("|"));
  if (rightIds.length) params.set("teamTwo", rightIds.join("|"));

  return `https://keeptradecut.com/trade-calculator?${params.toString()}`;
}

function OpenInKtcButton({ href }: { href: string }) {
  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{
        justifySelf: "center",
        border: "1px solid rgba(255,255,255,0.18)",
        background: "var(--dynasty-action-bg, rgba(255,255,255,0.92))",
        color: "var(--dynasty-action-text, #0f172a)",
        borderRadius: 10,
        padding: "9px 14px",
        fontSize: 12,
        fontWeight: 900,
        textDecoration: "none",
        boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
      }}
    >
      Open in KTC
    </a>
  );
}

export default function DynastyTradingPage(props: DynastyTradingPageProps = {}) {
  const {
    fixedLeagueIdentifier,
    hideFindTrade = false,
    hideLeagueSummary = false,
    hideTopBanner = false,
    hideTradeCalculations = false,
    fixedLeagueLoadingImageSrc,
    showOpenInKtc = false,
    theme = "default",
    loadingTitle = "Sleeper Dynasty League",
    loadingSubtitle = "Preparing the dynasty trading workspace.",
    persistLeagueIdentifier = true,
  } = props;
  const autoImportStartedRef = useRef(false);
  const mobilePlayerLookupRef = useRef<HTMLDivElement | null>(null);
  const mobileFindTradeMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileTradeMeterRef = useRef<HTMLDivElement | null>(null);
  const previousMobileActiveTabRef = useRef<"teams" | "trade" | "find">("teams");
  const isLeagueOfLegendsTheme = theme === "leagueOfLegends";
  const { isNarrowViewport, viewportHeight, viewportWidth } = useDynastyViewport({
    backgroundColor: isLeagueOfLegendsTheme ? "#050403" : "#08111d",
  });
  const [isTargetTeamMenuOpen, setIsTargetTeamMenuOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<"teams" | "trade" | "find">("teams");
  const [isMobileFindTradeMenuOpen, setIsMobileFindTradeMenuOpen] = useState(false);
  const [mobileTradeMeterHeight, setMobileTradeMeterHeight] = useState(220);
  const [fixedLeagueLoadingProgress, setFixedLeagueLoadingProgress] = useState(0);
  const [isFixedLeagueLoadingComplete, setIsFixedLeagueLoadingComplete] = useState(false);
  const [playerLookupQuery, setPlayerLookupQuery] = useState("");
  const [isPlayerLookupOpen, setIsPlayerLookupOpen] = useState(false);
  const [ktcPlayers, setKtcPlayers] = useState<Player[]>([]);
  const [isKtcLoaded, setIsKtcLoaded] = useState(false);
  const topBannerProps = {
    onExportRankings: noop,
    onExportCheatsheetPdf: noop,
    onImportRankings: noop,
    onOpenHowTo: noop,
    onOpenDraftSync: noop,
    uiScale: 1,
  };

  const { sleeperPlayers, canonicalizePlayers, resolveCanonicalId } = useSleeperDirectory({
    legacyPlayers: legacyBasePlayersArr,
  });

  const canonicalLegacyBasePlayers = useMemo(
    () => canonicalizePlayers(legacyBasePlayersArr),
    [canonicalizePlayers]
  );

  const basePlayers = useMemo(() => {
    const mergedById = new Map<string, Player>();
    for (const source of [canonicalLegacyBasePlayers, sleeperPlayers, ktcPlayers]) {
      for (const player of source) {
        const existing = mergedById.get(player.id);
        mergedById.set(player.id, {
          ...existing,
          ...player,
        });
      }
    }
    return Array.from(mergedById.values());
  }, [canonicalLegacyBasePlayers, sleeperPlayers, ktcPlayers]);

  const { playersById } = usePlayers({ basePlayers });
  const {
    handleImportLeague,
    importProgress,
    importedTeams,
    isImportingLeague,
    lastImportedAt,
    leagueError,
    leagueIdentifier,
    leagueName,
    leagueSeason,
    leagueSettings,
    setLeagueIdentifier,
  } = useDynastyLeagueImport({
    defaultLeagueIdentifier: fixedLeagueIdentifier,
    persistLeagueIdentifier,
    playersById,
    resolveCanonicalId,
  });
  const pickValueTemplates = useMemo(() => {
    const templates = new Map<string, { "1QB": number | null; "2QB": number | null }>();
    basePlayers.forEach((player) => {
      if (String((player as any).position ?? "").toUpperCase() !== "PI") return;
      if (!/\b(Early|Mid|Late)\b/i.test(player.name)) return;
      templates.set(player.name, {
        "1QB": player.value ?? null,
        "2QB": player.sfValue ?? player.value ?? null,
      });
    });
    return templates;
  }, [basePlayers]);
  const rookiePickOptions = useMemo(() => {
    return basePlayers
      .filter((player) => player.rookie && String(player.position ?? "").toUpperCase() !== "PI")
      .map((player) => ({
        id: player.id,
        name: player.name,
        position: player.position,
        team: player.team,
        value: (leagueSettings?.valueModeLabel ?? "2QB") === "2QB"
          ? player.sfValue ?? player.value ?? null
          : player.value ?? player.sfValue ?? null,
      }))
      .sort((left, right) => (right.value ?? Number.NEGATIVE_INFINITY) - (left.value ?? Number.NEGATIVE_INFINITY));
  }, [basePlayers, leagueSettings?.valueModeLabel]);
  const overallMaxPlayerValue = useMemo(() => {
    return basePlayers.reduce((maxValue, player) => {
      if (String(player.position ?? "").toUpperCase() === "PI") return maxValue;
      const candidateValue =
        (leagueSettings?.valueModeLabel ?? "2QB") === "2QB"
          ? player.sfValue ?? player.value ?? 0
          : player.value ?? player.sfValue ?? 0;
      return Math.max(maxValue, candidateValue);
    }, 1);
  }, [basePlayers, leagueSettings?.valueModeLabel]);
  const {
    activeRookiePickerId,
    calculateTwoTeamDisplaySummary,
    closeRoster,
    findTwoTeamAddPlayerValue,
    getAutoTargetTeamId,
    isEligibleColumn,
    isReceiveSelectionLocked,
    isTradeRowSelected,
    isTwoTeamTrade,
    openRoster,
    openRosterIds,
    openTeams,
    pickRookieAssignments,
    pickValueAssignments,
    removeTradeAsset,
    resetTradeWorkspace,
    resolveTradeAssetValue,
    seedRecommendedTrade,
    selectionMode,
    setActiveRookiePickerId,
    setOpenRosterIds,
    setPickRookieAssignments,
    setPickValueAssignments,
    setSelectionMode,
    setTradeSelections,
    singleOpenTeam,
    toggleTradeAsset,
    tradeSelections,
  } = useDynastyTradeWorkspace({
    importedTeams,
    isNarrowViewport,
    leagueSeason,
    leagueSettings,
    overallMaxPlayerValue,
    pickValueTemplates,
    setIsMobileFindTradeMenuOpen,
    setIsTargetTeamMenuOpen,
    setMobileActiveTab,
  });
  const {
    findTradeDirection,
    findTradePlayerId,
    findTradeRecommendations,
    findTradeTargetPosition,
    findTradeTargetTeamId,
    hasMoreFindTradeRecommendations,
    mobileFindTradeQuery,
    mobileFindTradeResults,
    playerLookupResults,
    selectedFindTradePlayer,
    selectedFindTradeTeam,
    selectedTargetTeam,
    setFindTradeDirection,
    setFindTradePlayerId,
    setFindTradeTargetPosition,
    setFindTradeTargetTeamId,
    setMobileFindTradeQuery,
    setVisibleFindTradeCount,
    targetableTeams,
    visibleFindTradeRecommendations,
  } = useDynastyFindTrade({
    importedTeams,
    isNarrowViewport,
    overallMaxPlayerValue,
    playerLookupQuery,
    resetKey: lastImportedAt,
    singleOpenTeam,
  });
  useEffect(() => {
    let cancelled = false;

    fetch(`${LOCAL_KTC_CSV_URL}?_t=${Date.now()}`, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error(`Runtime KTC fetch failed with HTTP ${response.status}`);
        return response.text();
      })
      .catch(() => fetch(`${rankingsCsvUrl}?_t=${Date.now()}`, { cache: "no-store" }).then((response) => response.text()))
      .then((csvText) => {
        if (cancelled) return;
        const parsed1qb = importRankingsCsv({ csvText, sortBy: "value" });
        const parsed2qb = importRankingsCsv({ csvText, sortBy: "sfvalue" });
        const pickPlayers = importPickPlayersFromCsv(csvText);
        setKtcPlayers(canonicalizePlayers([...(parsed1qb.players ?? []), ...(parsed2qb.players ?? []), ...pickPlayers]));
      })
      .catch(() => {
        if (!cancelled) setKtcPlayers([]);
      })
      .finally(() => {
        if (!cancelled) setIsKtcLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [canonicalizePlayers]);

  useEffect(() => {
    if (!fixedLeagueIdentifier) return;
    if (!isKtcLoaded) return;
    if (lastImportedAt || isImportingLeague || autoImportStartedRef.current) return;
    autoImportStartedRef.current = true;
    handleImportLeague();
  }, [fixedLeagueIdentifier, handleImportLeague, isImportingLeague, isKtcLoaded, lastImportedAt]);

  useEffect(() => {
    if (!lastImportedAt) return;
    resetTradeWorkspace();
  }, [lastImportedAt, resetTradeWorkspace]);

  useEffect(() => {
    if (!fixedLeagueIdentifier) return;
    setFixedLeagueLoadingProgress(0);
    setIsFixedLeagueLoadingComplete(false);

    const timers = [1, 2, 3, 4].map((second) =>
      window.setTimeout(() => {
        setFixedLeagueLoadingProgress(second * 25);
        if (second === 4) setIsFixedLeagueLoadingComplete(true);
      }, second * 1000)
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [fixedLeagueIdentifier]);

  const mobileTwoTeamMode = isNarrowViewport && isTwoTeamTrade;
  const mobileTwoTeamTradeMode = mobileTwoTeamMode && mobileActiveTab === "trade";
  const mobileFindTradeMode = !hideFindTrade && isNarrowViewport && mobileActiveTab === "find";
  const mobileTradeTabEnabled = !isNarrowViewport || openTeams.length === 2;
  const mobileFindTabEnabled = !hideFindTrade;
  const mobileTwoTeamColumnWidth = Math.max(165, Math.floor((viewportWidth - 38) / 2));
  const mobileRosterColumnWidth = isNarrowViewport
    ? openTeams.length === 2
      ? mobileTwoTeamColumnWidth
      : 288
    : DYNASTY_ROSTER_COLUMN_WIDTH;
  const mobileSetupPanelWidth = Math.max(320, viewportWidth - 28);
  const mobileTradeWorkspaceHeightPx = Math.max(420, viewportHeight - 170);
  const mobileTradeWorkspaceHeight = `${mobileTradeWorkspaceHeightPx}px`;
  const mobileTradeMeterReservedHeight = `${mobileTradeMeterHeight}px`;
  const twoTeamMeterCenterOffsetPx = 10;
  const mobileTradeMeterOverlay = mobileTwoTeamTradeMode && openTeams.length === 2 ? (() => {
    const [leftTeam, rightTeam] = openTeams;
    const leftIncomingBySource = tradeSelections[leftTeam.rosterId] ?? {};
    const leftIncomingAssets = Object.values(leftIncomingBySource).flat();
    const rightIncomingBySource = tradeSelections[rightTeam.rosterId] ?? {};
    const rightIncomingAssets = Object.values(rightIncomingBySource).flat();
    const {
      valueAdjustment,
      leftDisplayTotal,
      rightDisplayTotal,
      premiumSide,
    } = calculateTwoTeamDisplaySummary(leftIncomingAssets, rightIncomingAssets);
    const premiumTeamRosterId = premiumSide === "left" ? leftTeam.rosterId : rightTeam.rosterId;
    const favoredTeamRosterId = leftDisplayTotal >= rightDisplayTotal ? leftTeam.rosterId : rightTeam.rosterId;
    const favoredTeamName = favoredTeamRosterId === leftTeam.rosterId ? leftTeam.displayName : rightTeam.displayName;
    const addPlayerValue = findTwoTeamAddPlayerValue(leftIncomingAssets, rightIncomingAssets);
    const comparisonDenominator = Math.max(1, leftDisplayTotal + rightDisplayTotal);
    const comparisonDelta = leftDisplayTotal - rightDisplayTotal;
    const comparisonRatio = Math.max(-1, Math.min(1, comparisonDelta / comparisonDenominator));
    const balancePosition = 50 - comparisonRatio * 50;
    const meterFillLeft = Math.min(50, balancePosition);
    const meterFillRight = Math.max(50, balancePosition);
    const meterFillColor = "var(--dynasty-accent-fill, rgba(96,165,250,0.78))";
    const openInKtcHref = showOpenInKtc
      ? buildKtcTradeCalculatorUrl({
          format: leagueSettings?.valueModeLabel === "1QB" ? "1QB" : "2QB",
          leftIncomingAssets,
          rightIncomingAssets,
        })
      : "";

    return (
      <MobileTradeMeterOverlay
        addPlayerValue={addPlayerValue}
        currentLeagueSeason={leagueSeason}
        favoredTeamName={favoredTeamName}
        formatValue={formatValue}
        getPositionColor={getPositionColor}
        leftDisplayTotal={leftDisplayTotal}
        leftIncomingAssets={leftIncomingAssets}
        leftTeam={leftTeam}
        meterFillColor={meterFillColor}
        meterFillLeft={meterFillLeft}
        meterFillRight={meterFillRight}
        mobileTradeMeterRef={mobileTradeMeterRef}
        onChangePickBucket={(assetId, bucket) => {
          setPickValueAssignments((prev) => ({ ...prev, [assetId]: bucket }));
          setPickRookieAssignments((prev) => {
            const next = { ...prev };
            delete next[assetId];
            return next;
          });
        }}
        onRemoveTradeAsset={removeTradeAsset}
        onToggleRookiePicker={(assetId) => setActiveRookiePickerId((prev) => (prev === assetId ? null : assetId))}
        openInKtcHref={openInKtcHref}
        pickRookieAssignments={pickRookieAssignments}
        pickValueAssignments={pickValueAssignments}
        premiumTeamRosterId={premiumTeamRosterId}
        resolveTradeAssetValue={resolveTradeAssetValue}
        rightDisplayTotal={rightDisplayTotal}
        rightIncomingAssets={rightIncomingAssets}
        rightTeam={rightTeam}
        hideTradeCalculations={hideTradeCalculations}
        twoTeamMeterCenterOffsetPx={twoTeamMeterCenterOffsetPx}
        valueAdjustment={valueAdjustment}
      />
    );
  })() : null;

  useEffect(() => {
    if (hideFindTrade && mobileActiveTab === "find") {
      setMobileActiveTab("teams");
    }
  }, [hideFindTrade, mobileActiveTab]);
  useDynastyMobileUi({
    isNarrowViewport,
    mobileActiveTab,
    mobileFindTradeMenuRef,
    mobilePlayerLookupRef,
    mobileTradeMeterRef,
    mobileTwoTeamTradeMode,
    openTeams,
    previousMobileActiveTabRef,
    selectedFindTradePlayer,
    selectedFindTradeTeam,
    setActiveRookiePickerId,
    setIsMobileFindTradeMenuOpen,
    setIsPlayerLookupOpen,
    setMobileActiveTab,
    setMobileFindTradeQuery,
    setMobileTradeMeterHeight,
    setOpenRosterIds,
    setSelectionMode,
    setTradeSelections,
    tradeSelections,
  });
  const showFixedLeagueLoadingScreen = Boolean(fixedLeagueIdentifier && !isFixedLeagueLoadingComplete);

  return (
    <div
      className={isLeagueOfLegendsTheme ? "leagueOfLegendsTheme" : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: isNarrowViewport || hideTopBanner ? "100dvh" : "calc(100vh - var(--top-links-bar-height))",
        background: "var(--dynasty-shell-bg, linear-gradient(180deg, #0d1724 0%, #08111d 20%, #08111d 100%))",
        overscrollBehavior: isNarrowViewport ? "none" : undefined,
        paddingTop: isNarrowViewport ? "env(safe-area-inset-top)" : undefined,
      }}
    >
      {!isNarrowViewport && !hideTopBanner ? (
        <div className="appHeaderWrap">
          <TopBanner {...topBannerProps} />
        </div>
      ) : null}

      <div
        style={{
          padding: isNarrowViewport ? "8px 6px 10px" : "20px clamp(12px, 2vw, 24px) 24px",
          display: "flex",
          flexDirection: "column",
          gap: isNarrowViewport ? 8 : 18,
          flex: 1,
          minHeight: 0,
          overflowX: isNarrowViewport ? "auto" : "visible",
          overflowY: mobileTwoTeamTradeMode ? "hidden" : "visible",
          WebkitOverflowScrolling: isNarrowViewport ? "touch" : undefined,
          touchAction: isNarrowViewport ? "pan-x pan-y" : undefined,
          overscrollBehavior: isNarrowViewport ? "none" : undefined,
        }}
      >
        {showFixedLeagueLoadingScreen ? (
          <DynastyAutoLeagueStatusCard
            imageSrc={fixedLeagueLoadingImageSrc}
            importProgress={fixedLeagueLoadingProgress}
            isImportingLeague
            isNarrowViewport={isNarrowViewport}
            leagueError={leagueError}
            subtitle={loadingSubtitle}
            title={loadingTitle}
          />
        ) : !importedTeams.length ? (
          fixedLeagueIdentifier ? (
            <DynastyAutoLeagueStatusCard
              imageSrc={fixedLeagueLoadingImageSrc}
              importProgress={importProgress}
              isImportingLeague={isImportingLeague}
              isNarrowViewport={isNarrowViewport}
              leagueError={leagueError}
              subtitle={loadingSubtitle}
              title={loadingTitle}
            />
          ) : (
            <DynastyLeagueImportCard
              handleImportLeague={handleImportLeague}
              isImportingLeague={isImportingLeague}
              isNarrowViewport={isNarrowViewport}
              leagueError={leagueError}
              leagueIdentifier={leagueIdentifier}
              setLeagueIdentifier={setLeagueIdentifier}
            />
          )
        ) : null}

        {!showFixedLeagueLoadingScreen && importedTeams.length ? (
          <ImportedTeamsLayout>
            {isNarrowViewport ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${hideFindTrade ? 2 : 3}, minmax(0, 1fr))`,
                  gap: 6,
                }}
              >
                {([
                  ["teams", "Team Selection"],
                  ["trade", "Trade"],
                  ...(hideFindTrade ? [] : [["find", "Find Trade"] as const]),
                ] as const).map(([value, label]) => {
                  const selected = mobileActiveTab === value;
                  const disabled =
                    (value === "trade" && !mobileTradeTabEnabled) ||
                    (value === "find" && !mobileFindTabEnabled);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        if (disabled) return;
                        setMobileActiveTab(value);
                      }}
                      disabled={disabled}
                      style={{
                        border: selected ? "1px solid rgba(255,255,255,0.92)" : "1px solid rgba(255,255,255,0.12)",
                        background: selected ? "var(--dynasty-tab-selected-bg, rgba(255,255,255,0.92))" : "rgba(255,255,255,0.05)",
                        color: selected ? "var(--dynasty-tab-selected-text, #0f172a)" : "var(--text-0)",
                        borderRadius: 12,
                        padding: "10px 8px",
                        fontSize: 12,
                        fontWeight: 900,
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled && !selected ? 0.38 : 1,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : null}

            {!isNarrowViewport && !hideLeagueSummary ? (
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "grid", gap: 4 }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "var(--text-0)" }}>{leagueName}</div>
                  <div style={{ fontSize: 14, color: "rgba(255,255,255,0.62)" }}>
                    Teams are sorted by total {leagueSettings?.valueModeLabel ?? "KTC"} value from highest to lowest.
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(255,255,255,0.72)" }}>
                  {importedTeams.length} teams
                </div>
              </div>
            ) : null}

            {leagueSettings && !isNarrowViewport && !hideLeagueSummary ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                {[
                  leagueSettings.teamCount ? `${leagueSettings.teamCount} Teams` : null,
                  leagueSettings.starters ? `${leagueSettings.starters} Starters` : null,
                  leagueSettings.bench ? `${leagueSettings.bench} Bench` : null,
                  leagueSettings.taxi ? `${leagueSettings.taxi} Taxi` : null,
                  leagueSettings.ir ? `${leagueSettings.ir} IR` : null,
                  `${leagueSettings.valueModeLabel} Values`,
                  ...leagueSettings.formatTags.filter((tag) => tag !== leagueSettings.valueModeLabel),
                ]
                  .filter(Boolean)
                  .map((tag) => (
                    <div
                      key={String(tag)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.1)",
                        background: "rgba(255,255,255,0.05)",
                        color: "rgba(255,255,255,0.74)",
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 0.2,
                      }}
                    >
                      {tag}
                    </div>
                  ))}
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  isNarrowViewport
                    ? mobileActiveTab === "teams"
                      ? "minmax(0, 1fr)"
                      : "minmax(0, 1fr)"
                    : "220px minmax(0, 1fr)",
                gap: isNarrowViewport ? 6 : 12,
                alignItems: isNarrowViewport ? "stretch" : "start",
                minHeight: 0,
                flex: 1,
                height: mobileTwoTeamTradeMode ? mobileTradeWorkspaceHeight : undefined,
                width: isNarrowViewport ? "100%" : undefined,
                minWidth: isNarrowViewport ? "100%" : undefined,
                overflowX: isNarrowViewport ? "hidden" : "visible",
                overflowY: "hidden",
              }}
              onMouseDownCapture={(event) => {
                if (!selectionMode || isTwoTeamTrade) return;
                const target = event.target as HTMLElement | null;
                if (target?.closest("[data-roster-column='true']")) return;
                setSelectionMode(null);
              }}
            >
              <aside
                style={{
                  display: isNarrowViewport && mobileActiveTab !== "teams" ? "none" : "flex",
                  border: "1px solid var(--border-0)",
                  background: "var(--panel-bg)",
                  borderRadius: 14,
                  overflow: "hidden",
                  minHeight: 0,
                  height: isNarrowViewport ? "calc(100vh - 112px)" : undefined,
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: isNarrowViewport ? "10px 12px" : "12px 14px",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    display: "grid",
                    gap: isNarrowViewport ? 8 : 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: isNarrowViewport ? 11 : 12,
                      fontWeight: 900,
                      letterSpacing: 0.4,
                      color: "rgba(255,255,255,0.66)",
                      textTransform: "uppercase",
                    }}
                  >
                    Teams
                  </div>
                  {isNarrowViewport ? (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.6)",
                        lineHeight: 1.35,
                      }}
                    >
                      Select two teams and then go to trade tab.
                    </div>
                  ) : null}
                  <PlayerLookupPanel
                    isNarrowViewport={isNarrowViewport}
                    isOpen={isPlayerLookupOpen}
                    onClear={() => {
                      setPlayerLookupQuery("");
                      setIsPlayerLookupOpen(false);
                    }}
                    onFocus={() => setIsPlayerLookupOpen(true)}
                    onLookupResultClick={(rosterId) => {
                      openRoster(rosterId);
                      setIsPlayerLookupOpen(false);
                    }}
                    onQueryChange={(value) => {
                      setPlayerLookupQuery(value);
                      setIsPlayerLookupOpen(true);
                    }}
                    playerLookupQuery={playerLookupQuery}
                    results={playerLookupResults}
                    searchContainerRef={isNarrowViewport ? mobilePlayerLookupRef : undefined}
                  />
                </div>
                <div
                  className="dynastyRosterScroll"
                  style={{
                    display: "grid",
                    minHeight: 0,
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {importedTeams.map((team, index) => {
                    const isOpen = openRosterIds.includes(team.rosterId);
                    return (
                      <div
                        key={team.rosterId}
                        role="button"
                        tabIndex={isReceiveSelectionLocked ? -1 : 0}
                        onClick={() => {
                          if (isOpen) closeRoster(team.rosterId);
                          else openRoster(team.rosterId);
                        }}
                        onKeyDown={(event) => {
                          if (isReceiveSelectionLocked) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            if (isOpen) closeRoster(team.rosterId);
                            else openRoster(team.rosterId);
                          }
                        }}
                        style={{
                          border: "none",
                          borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.05)",
                          background: isOpen ? "rgba(255,255,255,0.08)" : "transparent",
                          color: "var(--text-0)",
                          textAlign: "left",
                          padding: isNarrowViewport ? "9px 12px" : "12px 14px",
                          cursor: isReceiveSelectionLocked ? "not-allowed" : "pointer",
                          display: "grid",
                          gap: isNarrowViewport ? 2 : 4,
                          opacity: isReceiveSelectionLocked && !isOpen ? 0.58 : 1,
                          outline: "none",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ fontSize: isNarrowViewport ? 9 : 10, fontWeight: 800, color: "rgba(255,255,255,0.5)" }}>#{index + 1}</div>
                          <div style={{ fontSize: isNarrowViewport ? 9 : 10, fontWeight: 800, color: "rgba(255,255,255,0.54)" }}>{formatValue(team.totalValue)}</div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: isNarrowViewport ? 7 : 8,
                            minWidth: 0,
                          }}
                        >
                          {isOpen ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                closeRoster(team.rosterId);
                              }}
                              disabled={isReceiveSelectionLocked}
                              aria-label={`Close ${team.displayName}`}
                              style={{
                                width: 18,
                                height: 18,
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                textAlign: "center",
                                fontSize: isNarrowViewport ? 15 : 16,
                                fontWeight: 900,
                                lineHeight: 1,
                                color: "#f87171",
                                flex: "0 0 18px",
                                cursor: isReceiveSelectionLocked ? "not-allowed" : "pointer",
                              }}
                            >
                              -
                            </button>
                          ) : (
                            <div
                              style={{
                                width: 18,
                                textAlign: "center",
                                fontSize: isNarrowViewport ? 15 : 16,
                                fontWeight: 900,
                                lineHeight: 1,
                                color: "#4ade80",
                                flex: "0 0 18px",
                              }}
                              aria-hidden="true"
                            >
                              +
                            </div>
                          )}
                          <TeamAvatar team={team} size={isNarrowViewport ? 20 : 22} />
                          <div
                            style={{
                              minWidth: 0,
                              fontSize: isNarrowViewport ? 12 : 13,
                              fontWeight: 800,
                              lineHeight: 1.25,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {team.displayName}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </aside>

              <div
                style={{
                  display:
                    isNarrowViewport && mobileActiveTab === "teams"
                      ? "none"
                      : mobileTwoTeamTradeMode || mobileFindTradeMode
                        ? "flex"
                        : undefined,
                  flexDirection: mobileTwoTeamTradeMode || mobileFindTradeMode ? "column" : undefined,
                  minWidth: isNarrowViewport ? "100%" : 0,
                  minHeight: 0,
                  height:
                    mobileTwoTeamTradeMode
                      ? mobileTradeWorkspaceHeight
                      : mobileFindTradeMode
                        ? "calc(100vh - 112px)"
                      : isNarrowViewport
                        ? "calc(100vh - 112px)"
                        : undefined,
                  overflowX: isNarrowViewport ? "hidden" : "auto",
                  overflowY: "hidden",
                  paddingBottom: mobileTwoTeamTradeMode ? 0 : isNarrowViewport ? 2 : 6,
                  WebkitOverflowScrolling: isNarrowViewport ? undefined : "touch",
                  touchAction: isNarrowViewport ? "auto" : "pan-x",
                  width: isNarrowViewport ? "100%" : undefined,
                }}
              >
                {selectionMode && !isTwoTeamTrade ? (
                  <div
                    style={{
                      position: "sticky",
                      left: 0,
                      marginBottom: 10,
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(7,10,18,0.92)",
                      color: "var(--text-0)",
                      fontSize: 12,
                      fontWeight: 800,
                      zIndex: 4,
                      width: "fit-content",
                    }}
                  >
                    Select players from any other open team. Click outside the roster columns to cancel.
                  </div>
                ) : null}

                {openTeams.length || mobileFindTradeMode ? (
                  <div
                    style={{
                      display:
                        mobileTwoTeamTradeMode
                          ? "block"
                          : isNarrowViewport && mobileActiveTab === "find"
                            ? "flex"
                            : "grid",
                      position: mobileTwoTeamTradeMode ? "relative" : undefined,
                      flexDirection: isNarrowViewport && mobileActiveTab === "find" ? "column" : undefined,
                      gridAutoFlow: mobileTwoTeamTradeMode ? undefined : "column",
                      gridAutoColumns: mobileTwoTeamTradeMode ? undefined : `${mobileRosterColumnWidth}px`,
                      gridTemplateColumns: mobileTwoTeamTradeMode ? "repeat(2, minmax(0, 1fr))" : undefined,
                      gap: isNarrowViewport ? 6 : 10,
                      alignItems: "stretch",
                      width: "fit-content",
                      minWidth: mobileTwoTeamTradeMode ? "100%" : isNarrowViewport ? "fit-content" : "100%",
                      height: mobileTwoTeamTradeMode || mobileFindTradeMode ? "100%" : undefined,
                      flex: mobileTwoTeamTradeMode || mobileFindTradeMode ? 1 : undefined,
                      minHeight: mobileTwoTeamTradeMode || mobileFindTradeMode ? 0 : undefined,
                    }}
                  >
                    {(!isNarrowViewport || mobileActiveTab === "trade") ? (
                      mobileTwoTeamTradeMode ? (
                        <div
                          className="dynastyRosterScroll"
                          style={{
                            gridColumn: "1 / -1",
                            gridRow: 1,
                            minHeight: 0,
                            height: "100%",
                            overflowY: "auto",
                            overflowX: "hidden",
                            WebkitOverflowScrolling: "touch",
                            boxSizing: "border-box",
                            paddingBottom: mobileTradeMeterReservedHeight,
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 0,
                            alignItems: "start",
                          }}
                        >
                          {openTeams.map((team, index) => {
                            return (
                      <article
                        key={team.rosterId}
                        data-roster-column="true"
                        style={{
                          border: "none",
                          background: "transparent",
                          borderRadius: 0,
                          overflow: "visible",
                          minWidth: 0,
                          minHeight: 0,
                          boxShadow: "none",
                          display: "flex",
                          flexDirection: "column",
                          borderLeft: index === 1 ? "1px solid rgba(255,255,255,0.08)" : "none",
                        }}
                      >
                          <div
                            style={{
                              padding: "8px 12px",
                              borderBottom: "1px solid rgba(255,255,255,0.08)",
                              background: "rgba(255,255,255,0.03)",
                              display: "grid",
                              gap: 8,
                            }}
                          >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              minWidth: 0,
                            }}
                          >
                            <TeamAvatar team={team} size={22} />
                            <div
                              style={{
                                minWidth: 0,
                                fontSize: 9.5,
                                fontWeight: 900,
                                color: "var(--text-0)",
                                lineHeight: 1.15,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              >
                                {team.displayName}
                              </div>
                            </div>
                        </div>

                        <div
                          style={{
                            padding: `8px 0 calc(${mobileTradeMeterReservedHeight} + 16px)`,
                            display: "block",
                          }}
                        >
                          <div
                            style={{
                              padding: "0 12px 8px",
                              display: "grid",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 900,
                                letterSpacing: 0.5,
                                color: "rgba(255,255,255,0.52)",
                                textTransform: "uppercase",
                              }}
                            >
                              Starters
                            </div>
                          </div>
                          {team.starters.map(({ slot, player }, starterIndex) => {
                            const isSelected = player ? isTradeRowSelected(team.rosterId, player.id) : false;
                            const isFindTradeSelected = openTeams.length === 1 && player?.id === findTradePlayerId;
                            return (
                            <div
                              key={`${team.rosterId}-starter-${slot}-${starterIndex}`}
                              onClick={() => {
                                if (!player) return;
                                if (openTeams.length === 1 && !isNarrowViewport) {
                                  if (!isTradeablePlayerAsset(player)) return;
                                  setFindTradePlayerId((prev) => (prev === player.id ? null : player.id));
                                  if (!findTradeTargetPosition) setFindTradeTargetPosition(player.position as FindTradeTargetPosition);
                                  return;
                                }
                                const targetTeamId = selectionMode?.teamId ?? getAutoTargetTeamId(team.rosterId);
                                if (!targetTeamId || !isEligibleColumn(team.rosterId)) return;
                                toggleTradeAsset(targetTeamId, team, player);
                              }}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                gap: 8,
                                alignItems: "center",
                                padding: "8px 10px",
                                borderTop: "1px solid rgba(255,255,255,0.05)",
                                cursor:
                                  openTeams.length === 1 && !isNarrowViewport
                                    ? player && isTradeablePlayerAsset(player)
                                      ? "pointer"
                                      : "default"
                                    : (selectionMode || isTwoTeamTrade) && player && isEligibleColumn(team.rosterId)
                                      ? "pointer"
                                      : "default",
                                background: isSelected || isFindTradeSelected ? "rgba(255,255,255,0.08)" : undefined,
                                opacity: isSelected ? 0.32 : 1,
                              }}
                            >
                              {(() => {
                                const isPick = String(player?.position ?? "").toUpperCase() === "PI";
                                return (
                              <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                                {!isPick ? (
                                  <div
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      overflow: "hidden",
                                      flex: "0 0 24px",
                                      background: "rgba(255,255,255,0.08)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {player?.imageUrl ? (
        <img
                                        src={player.imageUrl}
                                        alt={player.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 900,
                                          color: "rgba(255,255,255,0.7)",
                                        }}
                                      >
                                        {(player?.name ?? slot).slice(0, 1).toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                                <div style={{ minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 10.5,
                                      fontWeight: 800,
                                      color: "var(--text-0)",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {player?.name ?? "Open Slot"}
                                  </div>
                                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.52)", marginTop: 1 }}>
                                    {player ? (
                                      <>
                                        <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                                        {player.team ? <span>{` • ${player.team}`}</span> : null}
                                      </>
                                    ) : (
                                      "No eligible player"
                                    )}
                                  </div>
                                </div>
                              </div>
                                );
                              })()}
                              <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.82)", letterSpacing: "-0.01em" }}>
                                {formatValue(player?.value ?? null)}
                              </div>
                            </div>
                          )})}

                          <div
                            style={{
                              padding: "10px 12px 6px",
                              borderTop: "1px solid rgba(255,255,255,0.08)",
                              fontSize: 10,
                              fontWeight: 900,
                              letterSpacing: 0.5,
                              color: "rgba(255,255,255,0.52)",
                              textTransform: "uppercase",
                            }}
                          >
                            Bench
                          </div>
                          {team.benchPlayers.map((player) => {
                            const isSelected = isTradeRowSelected(team.rosterId, player.id);
                            const isFindTradeSelected = openTeams.length === 1 && player.id === findTradePlayerId;
                            return (
                            <div
                              key={`${team.rosterId}-bench-${player.id}-${player.name}`}
                              onClick={() => {
                                if (openTeams.length === 1 && !isNarrowViewport) {
                                  if (!isTradeablePlayerAsset(player)) return;
                                  setFindTradePlayerId((prev) => (prev === player.id ? null : player.id));
                                  if (!findTradeTargetPosition) setFindTradeTargetPosition(player.position as FindTradeTargetPosition);
                                  return;
                                }
                                const targetTeamId = selectionMode?.teamId ?? getAutoTargetTeamId(team.rosterId);
                                if (!targetTeamId || !isEligibleColumn(team.rosterId)) return;
                                toggleTradeAsset(targetTeamId, team, player);
                              }}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                gap: 8,
                                alignItems: "center",
                                padding: "8px 10px",
                                borderTop: "1px solid rgba(255,255,255,0.05)",
                                cursor:
                                  openTeams.length === 1 && !isNarrowViewport
                                    ? isTradeablePlayerAsset(player)
                                      ? "pointer"
                                      : "default"
                                    : (selectionMode || isTwoTeamTrade) && isEligibleColumn(team.rosterId)
                                      ? "pointer"
                                      : "default",
                                background: isSelected || isFindTradeSelected ? "rgba(255,255,255,0.08)" : undefined,
                                opacity: isSelected ? 0.32 : 1,
                              }}
                            >
                              {(() => {
                                const isPick = String(player.position ?? "").toUpperCase() === "PI";
                                return (
                              <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                                {!isPick ? (
                                  <div
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      overflow: "hidden",
                                      flex: "0 0 24px",
                                      background: "rgba(255,255,255,0.08)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {player.imageUrl ? (
                                      <img
                                        src={player.imageUrl}
                                        alt={player.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 900,
                                          color: "rgba(255,255,255,0.7)",
                                        }}
                                      >
                                        {player.name.slice(0, 1).toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                                <div style={{ minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 10.5,
                                      fontWeight: 800,
                                      color: "var(--text-0)",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {player.name}
                                  </div>
                                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.52)", marginTop: 1 }}>
                                    <>
                                      <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                                      {player.team ? <span>{` • ${player.team}`}</span> : null}
                                    </>
                                  </div>
                                </div>
                              </div>
                                );
                              })()}
                              <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.82)", letterSpacing: "-0.01em" }}>
                                {formatValue(player.value)}
                              </div>
                            </div>
                          )})}
                          <TeamTradeHistoryPanel compact team={team} />
                        </div>
                      </article>
                            );
                          })}
                        </div>
                      ) : openTeams.map((team) => {
                        return (
                      <article
                        key={team.rosterId}
                        data-roster-column="true"
                        style={{
                          border: "1px solid var(--border-0)",
                          background: "var(--panel-bg)",
                          borderRadius: 14,
                          overflow: "hidden",
                          minWidth: 0,
                          height: DYNASTY_COLUMN_HEIGHT,
                          boxShadow: "0 16px 34px rgba(0,0,0,0.18)",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                          <div
                            style={{
                              padding: isNarrowViewport ? "8px 12px" : "12px 14px 10px",
                              borderBottom: "1px solid rgba(255,255,255,0.08)",
                              background: "rgba(255,255,255,0.03)",
                              display: "grid",
                              gap: isNarrowViewport ? 8 : 6,
                            }}
                          >
                          {!isNarrowViewport ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.56)" }}>{team.playerCount} players</div>
                              <button
                                type="button"
                                onClick={() => closeRoster(team.rosterId)}
                                disabled={isReceiveSelectionLocked}
                                aria-label={`Close ${team.displayName}`}
                                style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 999,
                                  border: "1px solid rgba(255,255,255,0.12)",
                                  background: "rgba(255,255,255,0.06)",
                                  color: "var(--text-0)",
                                  fontSize: 13,
                                  fontWeight: 900,
                                  cursor: isReceiveSelectionLocked ? "not-allowed" : "pointer",
                                  lineHeight: 1,
                                  opacity: isReceiveSelectionLocked ? 0.45 : 1,
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ) : null}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: isNarrowViewport ? 7 : 8,
                              minWidth: 0,
                              paddingRight: !isNarrowViewport ? 30 : 0,
                            }}
                          >
                            <TeamAvatar team={team} size={isNarrowViewport ? 22 : 26} />
                            <div
                              style={{
                                minWidth: 0,
                                fontSize: isNarrowViewport ? 14 : 16,
                                fontWeight: 900,
                                color: "var(--text-0)",
                                lineHeight: 1.15,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              >
                                {team.displayName}
                              </div>
                            </div>
                            {!isNarrowViewport ? (
                              <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.74)" }}>
                                Total KTC: {formatValue(team.totalValue)}
                              </div>
                            ) : null}
                        </div>

                        <div
                          className="dynastyRosterScroll"
                          style={{
                            padding: "8px 0",
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                          }}
                        >
                          <div
                            style={{
                              padding: "0 12px 8px",
                              display: "grid",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 900,
                                letterSpacing: 0.5,
                                color: "rgba(255,255,255,0.52)",
                                textTransform: "uppercase",
                              }}
                            >
                              Starters
                            </div>
                          </div>
                          {team.starters.map(({ slot, player }, starterIndex) => {
                            const isSelected = player ? isTradeRowSelected(team.rosterId, player.id) : false;
                            const isFindTradeSelected = openTeams.length === 1 && player?.id === findTradePlayerId;
                            return (
                            <div
                              key={`${team.rosterId}-starter-${slot}-${starterIndex}`}
                              onClick={() => {
                                if (!player) return;
                                if (openTeams.length === 1 && !isNarrowViewport) {
                                  if (!isTradeablePlayerAsset(player)) return;
                                  setFindTradePlayerId((prev) => (prev === player.id ? null : player.id));
                                  if (!findTradeTargetPosition) setFindTradeTargetPosition(player.position as FindTradeTargetPosition);
                                  return;
                                }
                                const targetTeamId = selectionMode?.teamId ?? getAutoTargetTeamId(team.rosterId);
                                if (!targetTeamId || !isEligibleColumn(team.rosterId)) return;
                                toggleTradeAsset(targetTeamId, team, player);
                              }}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                gap: isNarrowViewport ? 8 : 10,
                                alignItems: "center",
                                padding: isNarrowViewport ? "8px 10px" : "10px 12px",
                                borderTop: "1px solid rgba(255,255,255,0.05)",
                                cursor:
                                  openTeams.length === 1 && !isNarrowViewport
                                    ? player && isTradeablePlayerAsset(player)
                                      ? "pointer"
                                      : "default"
                                    : (selectionMode || isTwoTeamTrade) && player && isEligibleColumn(team.rosterId)
                                      ? "pointer"
                                      : "default",
                                background: isSelected || isFindTradeSelected ? "rgba(255,255,255,0.08)" : undefined,
                                opacity: isSelected ? 0.32 : 1,
                              }}
                            >
                              {(() => {
                                const isPick = String(player?.position ?? "").toUpperCase() === "PI";
                                return (
                              <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: isNarrowViewport ? 6 : 8 }}>
                                <div
                                  style={{
                                    minWidth: isNarrowViewport ? 22 : 28,
                                    fontSize: isNarrowViewport ? 9 : 10,
                                    fontWeight: 900,
                                    color: getPositionColor(slot),
                                  }}
                                >
                                  <SlotLabel slot={slot} />
                                </div>
                                {!isPick ? (
                                  <div
                                    style={{
                                      width: isNarrowViewport ? 24 : 28,
                                      height: isNarrowViewport ? 24 : 28,
                                      borderRadius: "50%",
                                      overflow: "hidden",
                                      flex: isNarrowViewport ? "0 0 24px" : "0 0 28px",
                                      background: "rgba(255,255,255,0.08)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {player?.imageUrl ? (
                                      <img
                                        src={player.imageUrl}
                                        alt={player.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 900,
                                          color: "rgba(255,255,255,0.7)",
                                        }}
                                      >
                                        {(player?.name ?? slot).slice(0, 1).toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                                <div style={{ minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: isNarrowViewport ? 10.5 : 12,
                                      fontWeight: 800,
                                      color: "var(--text-0)",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {player?.name ?? "Open Slot"}
                                  </div>
                                  <div style={{ fontSize: isNarrowViewport ? 9 : 10, color: "rgba(255,255,255,0.52)", marginTop: isNarrowViewport ? 1 : 3 }}>
                                    {player ? (
                                      <>
                                        <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                                        {player.team ? <span>{` • ${player.team}`}</span> : null}
                                      </>
                                    ) : (
                                      "No eligible player"
                                    )}
                                  </div>
                                </div>
                              </div>
                                );
                              })()}
                              <div style={{ fontSize: isNarrowViewport ? 10 : 11, fontWeight: 800, color: "rgba(255,255,255,0.82)", letterSpacing: "-0.01em" }}>
                                {formatValue(player?.value ?? null)}
                              </div>
                            </div>
                          )})}

                          <div
                            style={{
                              padding: "10px 12px 6px",
                              borderTop: "1px solid rgba(255,255,255,0.08)",
                              fontSize: 10,
                              fontWeight: 900,
                              letterSpacing: 0.5,
                              color: "rgba(255,255,255,0.52)",
                              textTransform: "uppercase",
                            }}
                          >
                            Bench
                          </div>
                          {team.benchPlayers.map((player) => {
                            const isSelected = isTradeRowSelected(team.rosterId, player.id);
                            const isFindTradeSelected = openTeams.length === 1 && player.id === findTradePlayerId;
                            return (
                            <div
                              key={`${team.rosterId}-bench-${player.id}-${player.name}`}
                              onClick={() => {
                                if (openTeams.length === 1 && !isNarrowViewport) {
                                  if (!isTradeablePlayerAsset(player)) return;
                                  setFindTradePlayerId((prev) => (prev === player.id ? null : player.id));
                                  if (!findTradeTargetPosition) setFindTradeTargetPosition(player.position as FindTradeTargetPosition);
                                  return;
                                }
                                const targetTeamId = selectionMode?.teamId ?? getAutoTargetTeamId(team.rosterId);
                                if (!targetTeamId || !isEligibleColumn(team.rosterId)) return;
                                toggleTradeAsset(targetTeamId, team, player);
                              }}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                gap: isNarrowViewport ? 8 : 10,
                                alignItems: "center",
                                padding: isNarrowViewport ? "8px 10px" : "10px 12px",
                                borderTop: "1px solid rgba(255,255,255,0.05)",
                                cursor:
                                  openTeams.length === 1 && !isNarrowViewport
                                    ? isTradeablePlayerAsset(player)
                                      ? "pointer"
                                      : "default"
                                    : (selectionMode || isTwoTeamTrade) && isEligibleColumn(team.rosterId)
                                      ? "pointer"
                                      : "default",
                                background: isSelected || isFindTradeSelected ? "rgba(255,255,255,0.08)" : undefined,
                                opacity: isSelected ? 0.32 : 1,
                              }}
                            >
                              {(() => {
                                const isPick = String(player.position ?? "").toUpperCase() === "PI";
                                return (
                              <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: isNarrowViewport ? 6 : 8 }}>
                                <div
                                  style={{
                                    minWidth: isNarrowViewport ? 22 : 28,
                                    fontSize: isNarrowViewport ? 9 : 10,
                                    fontWeight: 900,
                                    color: "rgba(255,255,255,0.42)",
                                  }}
                                >
                                  BN
                                </div>
                                {!isPick ? (
                                  <div
                                    style={{
                                      width: isNarrowViewport ? 24 : 28,
                                      height: isNarrowViewport ? 24 : 28,
                                      borderRadius: "50%",
                                      overflow: "hidden",
                                      flex: isNarrowViewport ? "0 0 24px" : "0 0 28px",
                                      background: "rgba(255,255,255,0.08)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {player.imageUrl ? (
                                      <img
                                        src={player.imageUrl}
                                        alt={player.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 900,
                                          color: "rgba(255,255,255,0.7)",
                                        }}
                                      >
                                        {player.name.slice(0, 1).toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                                <div style={{ minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: isNarrowViewport ? 10.5 : 12,
                                      fontWeight: 800,
                                      color: "var(--text-0)",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {player.name}
                                  </div>
                                  <div style={{ fontSize: isNarrowViewport ? 9 : 10, color: "rgba(255,255,255,0.52)", marginTop: isNarrowViewport ? 1 : 3 }}>
                                    <>
                                      <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                                      {player.team ? <span>{` • ${player.team}`}</span> : null}
                                    </>
                                  </div>
                                </div>
                              </div>
                                );
                              })()}
                              <div style={{ fontSize: isNarrowViewport ? 10 : 11, fontWeight: 800, color: "rgba(255,255,255,0.82)", letterSpacing: "-0.01em" }}>
                                {formatValue(player.value)}
                              </div>
                            </div>
                          )})}
                          <TeamTradeHistoryPanel compact={isNarrowViewport} team={team} />
                        </div>
                      </article>
                      );
                    })) : null}

                    <aside
                      style={{
                        display: mobileTwoTeamTradeMode ? "none" : "grid",
                        width:
                          mobileTwoTeamTradeMode
                            ? "100%"
                            : isNarrowViewport
                              ? mobileSetupPanelWidth
                              : DYNASTY_SETUP_COLUMN_WIDTH,
                        minWidth: 0,
                        gridTemplateRows: "auto minmax(0, 1fr)",
                        gap: 10,
                        alignSelf: mobileTwoTeamTradeMode ? "stretch" : "start",
                        height:
                          mobileTwoTeamTradeMode
                            ? mobileTradeMeterReservedHeight
                            : mobileFindTradeMode
                              ? "100%"
                              : isNarrowViewport
                              ? "100%"
                              : DYNASTY_COLUMN_HEIGHT,
                        gridColumn: mobileTwoTeamTradeMode ? "1 / -1" : undefined,
                        position: mobileTwoTeamTradeMode ? "fixed" : undefined,
                        left: mobileTwoTeamTradeMode ? 0 : undefined,
                        right: mobileTwoTeamTradeMode ? 0 : undefined,
                        bottom: mobileTwoTeamTradeMode ? 0 : undefined,
                        zIndex: mobileTwoTeamTradeMode ? 12 : undefined,
                        paddingBottom: mobileTwoTeamTradeMode ? "env(safe-area-inset-bottom, 0px)" : undefined,
                        background: mobileTwoTeamTradeMode ? "var(--dynasty-trade-panel-bg, rgba(20,28,46,0.98))" : undefined,
                      }}
                    >
                      {!isNarrowViewport ? (
                        <div
                          style={{
                            padding: "0 4px",
                            fontSize: 12,
                            fontWeight: 900,
                            letterSpacing: 0.4,
                            color: "rgba(255,255,255,0.66)",
                            textTransform: "uppercase",
                          }}
                        >
                          {openTeams.length === 1 && !hideFindTrade ? "Find Trades" : "Trade Setup"}
                        </div>
                      ) : null}
                      {!hideFindTrade && ((openTeams.length === 1 && singleOpenTeam) || mobileFindTradeMode) ? (
                        <div
                          style={{
                            border: "1px solid var(--border-0)",
                            background: "var(--panel-bg)",
                            borderRadius: 14,
                            boxShadow: "0 16px 34px rgba(0,0,0,0.18)",
                            display: "grid",
                            gridTemplateRows: "auto auto minmax(0, 1fr)",
                            minHeight: 0,
                            overflow: "hidden",
                          }}
                        >
                          <div style={{ padding: "14px", display: "grid", gap: 12, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                            <div style={{ display: "grid", gap: 4 }}>
                              <div style={{ fontSize: 18, fontWeight: 900, color: "var(--text-0)" }}>Find Trades</div>
                            </div>

                            {isNarrowViewport ? (
                              <div
                                ref={mobileFindTradeMenuRef}
                                style={{ display: "grid", gap: 8, position: "relative" }}
                              >
                                <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,0.56)", textTransform: "uppercase" }}>
                                  Player Search
                                </div>
                                <div style={{ position: "relative" }}>
                                  <input
                                    value={mobileFindTradeQuery}
                                    onChange={(event) => {
                                      setMobileFindTradeQuery(event.target.value);
                                      setIsMobileFindTradeMenuOpen(true);
                                      if (!event.target.value.trim()) setFindTradePlayerId(null);
                                    }}
                                    onFocus={() => setIsMobileFindTradeMenuOpen(true)}
                                    placeholder="Search any player"
                                    style={{
                                      width: "100%",
                                      borderRadius: 10,
                                      border: "1px solid rgba(255,255,255,0.12)",
                                      background: "rgba(255,255,255,0.06)",
                                      color: "var(--text-0)",
                                      padding: "10px 34px 10px 12px",
                                      outline: "none",
                                      boxSizing: "border-box",
                                      fontSize: 16,
                                    }}
                                  />
                                  {mobileFindTradeQuery ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setMobileFindTradeQuery("");
                                        setFindTradePlayerId(null);
                                        setIsMobileFindTradeMenuOpen(false);
                                      }}
                                      aria-label="Clear find trade player"
                                      style={{
                                        position: "absolute",
                                        right: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        width: 18,
                                        height: 18,
                                        borderRadius: 999,
                                        border: "none",
                                        background: "rgba(255,255,255,0.12)",
                                        color: "rgba(255,255,255,0.78)",
                                        fontSize: 12,
                                        fontWeight: 900,
                                        cursor: "pointer",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0,
                                        lineHeight: 1,
                                      }}
                                    >
                                      ×
                                    </button>
                                  ) : null}
                                </div>
                                {isMobileFindTradeMenuOpen ? (
                                  <div
                                    style={{
                                      borderRadius: 12,
                                      border: "1px solid rgba(255,255,255,0.12)",
                                      background: "var(--dynasty-menu-bg, rgba(18,22,34,0.98))",
                                      boxShadow: "0 18px 34px rgba(0,0,0,0.28)",
                                      overflow: "hidden",
                                      display: "grid",
                                      maxHeight: 220,
                                      overflowY: "auto",
                                    }}
                                  >
                                    {mobileFindTradeResults.length ? (
                                      mobileFindTradeResults.map(({ team, player }) => (
                                        <button
                                          key={`mobile-find-player-${player.id}`}
                                          type="button"
                                          onClick={() => {
                                            setFindTradePlayerId(player.id);
                                            setMobileFindTradeQuery(player.name);
                                            setIsMobileFindTradeMenuOpen(false);
                                            if (!findTradeTargetPosition) {
                                              setFindTradeTargetPosition(player.position as FindTradeTargetPosition);
                                            }
                                          }}
                                          style={{
                                            border: "none",
                                            borderTop: "1px solid rgba(255,255,255,0.04)",
                                            background: player.id === findTradePlayerId ? "rgba(255,255,255,0.08)" : "transparent",
                                            color: "var(--text-0)",
                                            padding: "10px 12px",
                                            textAlign: "left",
                                            fontSize: 12,
                                            cursor: "pointer",
                                            display: "grid",
                                            gap: 3,
                                          }}
                                        >
                                          <div style={{ fontWeight: 800 }}>{player.name}</div>
                                          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.54)" }}>
                                            <span>{team.displayName}</span>
                                            <span style={{ color: "rgba(255,255,255,0.42)" }}> • </span>
                                            <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                                            {player.team ? <span>{` • ${player.team}`}</span> : null}
                                            <span>{` • ${formatValue(player.value)}`}</span>
                                          </div>
                                        </button>
                                      ))
                                    ) : (
                                      <div style={{ padding: "12px", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                                        No matching players found on this roster.
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}

                            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
                              {([
                                ["up", "Up-tier"],
                                ["down", "Down-tier"],
                              ] as const).map(([value, label]) => {
                                const selected = findTradeDirection === value;
                                return (
                                  <button
                                    key={value}
                                    type="button"
                                    onClick={() => setFindTradeDirection(value)}
                                    style={{
                                      border: "1px solid rgba(255,255,255,0.12)",
                                      background: selected ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      color: "var(--text-0)",
                                      textAlign: "left",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      minHeight: 42,
                                    }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                      <div
                                        style={{
                                          width: 18,
                                          height: 18,
                                          borderRadius: "50%",
                                          border: "2px solid rgba(255,255,255,0.72)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flex: "0 0 18px",
                                        }}
                                      >
                                        {selected ? (
                                          <div
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              background: "var(--text-0)",
                                            }}
                                          />
                                        ) : null}
                                      </div>
                                      <div style={{ fontSize: 14, fontWeight: 900 }}>{label}</div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>

                            <div style={{ display: "grid", gap: 8 }}>
                              <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,0.56)", textTransform: "uppercase" }}>
                                Target Position
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                                {(["QB", "RB", "WR", "TE"] as FindTradeTargetPosition[]).map((position) => {
                                  const selected = findTradeTargetPosition === position;
                                  return (
                                    <button
                                      key={position}
                                      type="button"
                                      onClick={() => setFindTradeTargetPosition(position)}
                                      style={{
                                        border: selected ? "1px solid rgba(255,255,255,0.92)" : "1px solid rgba(255,255,255,0.12)",
                                        background: selected ? "var(--dynasty-tab-selected-bg, rgba(255,255,255,0.92))" : "rgba(255,255,255,0.05)",
                                        color: getPositionColor(position),
                                        borderRadius: 10,
                                        padding: "10px 0",
                                        fontSize: 12,
                                        fontWeight: 900,
                                        cursor: "pointer",
                                      }}
                                    >
                                      {position}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div
                              style={{ display: "grid", gap: 8, position: "relative" }}
                              onBlur={(event) => {
                                const nextFocused = event.relatedTarget as Node | null;
                                if (nextFocused && event.currentTarget.contains(nextFocused)) return;
                                setIsTargetTeamMenuOpen(false);
                              }}
                            >
                              <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,0.56)", textTransform: "uppercase" }}>
                                Target Team
                              </div>
                              <button
                                type="button"
                                onClick={() => setIsTargetTeamMenuOpen((prev) => !prev)}
                                style={{
                                  width: "100%",
                                  borderRadius: 10,
                                  border: "1px solid rgba(255,255,255,0.12)",
                                  background: "rgba(255,255,255,0.06)",
                                  color: "var(--text-0)",
                                  padding: "10px 12px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 10,
                                  cursor: "pointer",
                                }}
                              >
                                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                  {selectedTargetTeam ? <TeamAvatar team={selectedTargetTeam} size={20} /> : null}
                                  <span
                                    style={{
                                      minWidth: 0,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {selectedTargetTeam ? selectedTargetTeam.displayName : "All teams"}
                                  </span>
                                </span>
                                <span style={{ color: "rgba(255,255,255,0.56)", fontSize: 10 }}>▼</span>
                              </button>
                              {isTargetTeamMenuOpen ? (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    marginTop: 6,
                                    borderRadius: 12,
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    background: "var(--dynasty-menu-bg, rgba(18,22,34,0.98))",
                                    boxShadow: "0 18px 34px rgba(0,0,0,0.28)",
                                    overflow: "hidden",
                                    zIndex: 8,
                                    display: "grid",
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFindTradeTargetTeamId("");
                                      setIsTargetTeamMenuOpen(false);
                                    }}
                                    style={{
                                      border: "none",
                                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                                      background: "transparent",
                                      color: "var(--text-0)",
                                      padding: "10px 12px",
                                      textAlign: "left",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: "pointer",
                                    }}
                                  >
                                    All teams
                                  </button>
                                  {targetableTeams.map((team) => (
                                    <button
                                      key={`target-team-${team.rosterId}`}
                                      type="button"
                                      onClick={() => {
                                        setFindTradeTargetTeamId(String(team.rosterId));
                                        setIsTargetTeamMenuOpen(false);
                                      }}
                                      style={{
                                        border: "none",
                                        borderTop: "1px solid rgba(255,255,255,0.04)",
                                        background: String(team.rosterId) === findTradeTargetTeamId ? "rgba(255,255,255,0.08)" : "transparent",
                                        color: "var(--text-0)",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                      }}
                                    >
                                      <TeamAvatar team={team} size={20} />
                                      <span
                                        style={{
                                          minWidth: 0,
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {team.displayName}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>

                            {selectedFindTradePlayer ? (
                              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                                Building around <span style={{ color: "var(--text-0)", fontWeight: 900 }}>{selectedFindTradePlayer.name}</span>
                                {" "}from <span style={{ color: "var(--text-0)", fontWeight: 900 }}>{selectedFindTradeTeam?.displayName ?? "selected team"}</span>
                                {" "}({selectedFindTradePlayer.position}, {formatValue(selectedFindTradePlayer.value)}).
                              </div>
                            ) : null}
                          </div>

                          {findTradeRecommendations.length ? (
                            <div
                              style={{
                                padding: "10px 14px",
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                                fontSize: 11,
                                fontWeight: 800,
                                color: "rgba(255,255,255,0.58)",
                              }}
                            >
                              {`${findTradeRecommendations.length} recommended trades. Click any card to open both teams and seed the trade window.`}
                            </div>
                          ) : null}

                          <div
                            className="dynastyRosterScroll"
                            style={{
                              minHeight: 0,
                              height: "100%",
                              overflowY: "auto",
                              overflowX: "hidden",
                              WebkitOverflowScrolling: "touch",
                              touchAction: "pan-y",
                              padding: 14,
                              paddingBottom: 18,
                              display: "grid",
                              gap: 10,
                              alignContent: "start",
                              borderBottomLeftRadius: 14,
                              borderBottomRightRadius: 14,
                            }}
                          >
                            {visibleFindTradeRecommendations.length ? (
                              visibleFindTradeRecommendations.map((recommendation) => {
                                const partnerTeam = importedTeams.find((team) => team.rosterId === recommendation.partnerTeamId);
                                return (
                                  <button
                                    key={recommendation.id}
                                    type="button"
                                    onClick={() => seedRecommendedTrade(recommendation)}
                                    style={{
                                      border: "1px solid rgba(255,255,255,0.1)",
                                      background: "rgba(255,255,255,0.04)",
                                      borderRadius: 14,
                                      padding: "12px 12px 10px",
                                      color: "var(--text-0)",
                                      cursor: "pointer",
                                      textAlign: "left",
                                      display: "grid",
                                      gap: 10,
                                    }}
                                  >
                                    <div style={{ fontSize: 13, fontWeight: 900 }}>
                                      {(selectedFindTradeTeam?.displayName ?? "Selected Team")} vs {partnerTeam?.displayName ?? `Roster ${recommendation.partnerTeamId}`}
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", gap: 10, alignItems: "start" }}>
                                      <div style={{ display: "grid", gap: 6 }}>
                                        <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>You Send</div>
                                        {recommendation.myAssets.map((player) => (
                                          <div key={`${recommendation.id}-send-${player.id}`} style={{ fontSize: 12, color: "rgba(255,255,255,0.88)" }}>
                                            {player.name}
                                            <span style={{ color: "rgba(255,255,255,0.48)" }}> • </span>
                                            <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                                            <span style={{ color: "rgba(255,255,255,0.48)" }}>{` • ${formatValue(player.value)}`}</span>
                                          </div>
                                        ))}
                                      </div>
                                      <div style={{ alignSelf: "center", fontSize: 14, fontWeight: 900, color: "rgba(255,255,255,0.46)" }}>for</div>
                                      <div style={{ display: "grid", gap: 6 }}>
                                        <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>You Receive</div>
                                        {recommendation.partnerAssets.map((player) => (
                                          <div key={`${recommendation.id}-receive-${player.id}`} style={{ fontSize: 12, color: "rgba(255,255,255,0.88)" }}>
                                            {player.name}
                                            <span style={{ color: "rgba(255,255,255,0.48)" }}> • </span>
                                            <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                                            <span style={{ color: "rgba(255,255,255,0.48)" }}>{` • ${formatValue(player.value)}`}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </button>
                                );
                              })
                            ) : (
                              <div
                                style={{
                                  minHeight: 180,
                                  border: "1px dashed rgba(255,255,255,0.12)",
                                  borderRadius: 14,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  padding: 20,
                                  color: "rgba(255,255,255,0.5)",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  background: "rgba(255,255,255,0.02)",
                                }}
                              >
                                {!selectedFindTradePlayer
                                  ? isNarrowViewport
                                    ? "Search for one player from any team to start finding trades."
                                    : "Select one player from your open roster to start finding trades."
                                  : !findTradeTargetPosition
                                    ? "Choose a target position to generate trade ideas."
                                  : "No close trade matches found yet for that setup."}
                              </div>
                            )}
                            {hasMoreFindTradeRecommendations ? (
                              <button
                                type="button"
                                onClick={() => setVisibleFindTradeCount((prev) => prev + 10)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  padding: "2px 0 6px",
                                  color: "var(--dynasty-accent, #60a5fa)",
                                  fontSize: 12,
                                  fontWeight: 800,
                                  cursor: "pointer",
                                  textAlign: "center",
                                  textDecoration: "underline",
                                }}
                              >
                                load more trades
                              </button>
                            ) : visibleFindTradeRecommendations.length ? (
                              <div
                                style={{
                                  padding: "2px 0 6px",
                                  color: "rgba(255,255,255,0.52)",
                                  fontSize: 12,
                                  fontWeight: 800,
                                  textAlign: "center",
                                }}
                              >
                                no more trades
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : mobileTwoTeamTradeMode ? null : (
                      <div
                        style={{
                          border: mobileTwoTeamTradeMode || (!isNarrowViewport && openTeams.length === 2) ? "none" : "1px solid var(--border-0)",
                          background: mobileTwoTeamTradeMode ? "var(--dynasty-trade-panel-bg, rgba(20,28,46,0.98))" : !isNarrowViewport && openTeams.length === 2 ? "transparent" : "var(--panel-bg)",
                          borderRadius: mobileTwoTeamTradeMode || (!isNarrowViewport && openTeams.length === 2) ? 0 : 14,
                          boxShadow: mobileTwoTeamTradeMode ? "0 -12px 30px rgba(0,0,0,0.32)" : !isNarrowViewport && openTeams.length === 2 ? "none" : "0 16px 34px rgba(0,0,0,0.18)",
                          minHeight: 0,
                          overflow: "hidden",
                          height: mobileTwoTeamTradeMode ? "100%" : undefined,
                        }}
                      >
                        <div
                          className="dynastyRosterScroll"
                          style={{
                            minHeight: 0,
                            height: "100%",
                            overflowY: "auto",
                            overflowX: "hidden",
                            padding: mobileTwoTeamTradeMode ? 0 : !isNarrowViewport && openTeams.length === 2 ? 0 : 10,
                            display: "grid",
                            gap: 10,
                          }}
                        >
                        {openTeams.length === 2 && !isNarrowViewport ? (() => {
                          const [leftTeam, rightTeam] = openTeams;
                          const leftIncomingBySource = tradeSelections[leftTeam.rosterId] ?? {};
                          const leftIncomingAssets = Object.values(leftIncomingBySource).flat();
                          const rightIncomingBySource = tradeSelections[rightTeam.rosterId] ?? {};
                          const rightIncomingAssets = Object.values(rightIncomingBySource).flat();
                          const {
                            valueAdjustment,
                            leftDisplayTotal,
                            rightDisplayTotal,
                            premiumSide,
                          } = calculateTwoTeamDisplaySummary(leftIncomingAssets, rightIncomingAssets);
                          const premiumTeamRosterId = premiumSide === "left" ? leftTeam.rosterId : rightTeam.rosterId;
                          const favoredTeamRosterId = leftDisplayTotal >= rightDisplayTotal ? leftTeam.rosterId : rightTeam.rosterId;
                          const favoredTeamName = favoredTeamRosterId === leftTeam.rosterId ? leftTeam.displayName : rightTeam.displayName;
                          const addPlayerValue = findTwoTeamAddPlayerValue(leftIncomingAssets, rightIncomingAssets);
                          const comparisonDenominator = Math.max(1, leftDisplayTotal + rightDisplayTotal);
                          const comparisonDelta = leftDisplayTotal - rightDisplayTotal;
                          const comparisonRatio = Math.max(-1, Math.min(1, comparisonDelta / comparisonDenominator));
                          const balancePosition = 50 - comparisonRatio * 50;
                          const meterFillLeft = Math.min(50, balancePosition);
                          const meterFillRight = Math.max(50, balancePosition);
                          const meterFillColor = "var(--dynasty-accent-fill, rgba(96,165,250,0.78))";
                          const openInKtcHref = showOpenInKtc
                            ? buildKtcTradeCalculatorUrl({
                                format: leagueSettings?.valueModeLabel === "1QB" ? "1QB" : "2QB",
                                leftIncomingAssets,
                                rightIncomingAssets,
                              })
                            : "";

                          return (
                            <div
                              key={`desktop-two-team-comparison-${leftIncomingAssets.length}-${rightIncomingAssets.length}`}
                              style={{
                                minHeight: 0,
                                display: "grid",
                                gap: 0,
                                alignContent: "start",
                              }}
                            >
                              <div
                                style={{
                                  border: "1px solid rgba(255,255,255,0.08)",
                                  background: "var(--dynasty-trade-panel-bg, rgba(20,28,46,0.98))",
                                  borderRadius: 18,
                                  boxShadow: "0 16px 34px rgba(0,0,0,0.18)",
                                  padding: "18px 16px 14px",
                                  display: "grid",
                                  gap: 14,
                                }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                    gap: 14,
                                    alignItems: "start",
                                  }}
                                >
                                  {[
                                    {
                                      team: leftTeam,
                                      incomingAssets: leftIncomingAssets,
                                      displayReceiveValue: leftDisplayTotal,
                                      displayedValueAdjustment: leftTeam.rosterId === premiumTeamRosterId ? valueAdjustment : 0,
                                    },
                                    {
                                      team: rightTeam,
                                      incomingAssets: rightIncomingAssets,
                                      displayReceiveValue: rightDisplayTotal,
                                      displayedValueAdjustment: rightTeam.rosterId === premiumTeamRosterId ? valueAdjustment : 0,
                                    },
                                  ].map(({ team, incomingAssets, displayReceiveValue, displayedValueAdjustment }) => (
                                    <div
                                      key={`desktop-comparison-${team.rosterId}`}
                                      style={{
                                        display: "grid",
                                        gap: 10,
                                        minWidth: 0,
                                        alignContent: "start",
                                        borderLeft: team.rosterId === rightTeam.rosterId ? "2px solid rgba(148,163,184,0.22)" : "none",
                                        paddingLeft: team.rosterId === rightTeam.rosterId ? 14 : 0,
                                      }}
                                    >
                                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                        <TeamAvatar team={team} size={22} />
                                        <div
                                          style={{
                                            minWidth: 0,
                                            fontSize: 13,
                                            fontWeight: 900,
                                            color: "var(--text-0)",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {team.displayName}
                                        </div>
                                      </div>

                                      <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>
                                        Receive
                                      </div>

                                      {incomingAssets.length ? (
                                        incomingAssets.map((asset) => (
                                          <TradeAssetCard
                                            key={`desktop-comparison-receive-${team.rosterId}-${asset.sourceTeamId}-${asset.id}`}
                                            asset={asset}
                                            onClick={() => removeTradeAsset(team.rosterId, asset)}
                                            resolvedValue={resolveTradeAssetValue(asset)}
                                            pickBucket={pickValueAssignments[asset.id] ?? null}
                                            assignedRookie={pickRookieAssignments[asset.id] ?? null}
                                            onToggleRookiePicker={() =>
                                              setActiveRookiePickerId((prev) => (prev === asset.id ? null : asset.id))
                                            }
                                            onPickBucketChange={(bucket) => {
                                              setPickValueAssignments((prev) => ({ ...prev, [asset.id]: bucket }));
                                              setPickRookieAssignments((prev) => {
                                                const next = { ...prev };
                                                delete next[asset.id];
                                                return next;
                                              });
                                            }}
                                            currentLeagueSeason={leagueSeason}
                                            formatValue={formatValue}
                                            getPositionColor={getPositionColor}
                                          />
                                        ))
                                      ) : (
                                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.42)" }}>No players selected</div>
                                      )}

                                      <div style={{ minWidth: 0, display: "grid", gap: 2 }}>
                                        {!hideTradeCalculations && displayedValueAdjustment > 0 ? (
                                          <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.52)" }}>
                                            Value adjustment (+{formatValue(Math.round(displayedValueAdjustment))})
                                          </div>
                                        ) : (
                                          !hideTradeCalculations ? <div style={{ height: 14 }} /> : null
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {!hideTradeCalculations ? (
                                  <div
                                  style={{
                                    paddingTop: 12,
                                    borderTop: "1px solid rgba(255,255,255,0.08)",
                                    display: "grid",
                                    gap: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                      gap: 14,
                                      alignItems: "end",
                                    }}
                                  >
                                    <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.88)" }}>
                                      {formatValue(Math.round(leftDisplayTotal))}
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.88)", textAlign: "right" }}>
                                      {formatValue(Math.round(rightDisplayTotal))}
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      position: "relative",
                                      height: 28,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        height: 8,
                                        borderRadius: 999,
                                        overflow: "hidden",
                                        background: "rgba(255,255,255,0.08)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: `${meterFillLeft}%`,
                                          top: 0,
                                          bottom: 0,
                                          right: `${100 - meterFillRight}%`,
                                          background: meterFillColor,
                                          transition: "left 220ms ease, right 220ms ease",
                                        }}
                                      />
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: "50%",
                                          top: "50%",
                                          transform: "translate(-50%, -50%)",
                                          width: 4,
                                          height: 18,
                                          borderRadius: 999,
                                          background: "rgba(255,255,255,0.32)",
                                          boxShadow: "0 0 0 1px rgba(7,10,18,0.38)",
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 900,
                                      textAlign: "center",
                                      color: "var(--dynasty-accent, #60a5fa)",
                                    }}
                                  >
                                    {addPlayerValue > 0
                                      ? `${favoredTeamName} must add player worth ${formatValue(addPlayerValue)} for even trade.`
                                      : "Trade is even."}
                                  </div>
                                  </div>
	                                ) : null}
                                  {showOpenInKtc ? <OpenInKtcButton href={openInKtcHref} /> : null}
	                            </div>
                            </div>
	                          );
                        })() : isNarrowViewport && openTeams.length === 2 && !mobileTwoTeamTradeMode ? (() => {
                          const [leftTeam, rightTeam] = openTeams;
                          const leftIncomingBySource = tradeSelections[leftTeam.rosterId] ?? {};
                          const leftIncomingAssets = Object.values(leftIncomingBySource).flat();
                          const rightIncomingBySource = tradeSelections[rightTeam.rosterId] ?? {};
                          const rightIncomingAssets = Object.values(rightIncomingBySource).flat();
                          const {
                            valueAdjustment,
                            leftDisplayTotal,
                            rightDisplayTotal,
                            premiumSide,
                          } = calculateTwoTeamDisplaySummary(leftIncomingAssets, rightIncomingAssets);
                          const premiumTeamRosterId = premiumSide === "left" ? leftTeam.rosterId : rightTeam.rosterId;
                          const favoredTeamRosterId = leftDisplayTotal >= rightDisplayTotal ? leftTeam.rosterId : rightTeam.rosterId;
                          const favoredTeamName = favoredTeamRosterId === leftTeam.rosterId ? leftTeam.displayName : rightTeam.displayName;
                          const addPlayerValue = findTwoTeamAddPlayerValue(leftIncomingAssets, rightIncomingAssets);
                          const comparisonDenominator = Math.max(1, leftDisplayTotal + rightDisplayTotal);
                          const comparisonDelta = leftDisplayTotal - rightDisplayTotal;
                          const comparisonRatio = Math.max(-1, Math.min(1, comparisonDelta / comparisonDenominator));
                          const balancePosition = 50 - comparisonRatio * 50;
                          const meterFillLeft = Math.min(50, balancePosition);
                          const meterFillRight = Math.max(50, balancePosition);
                          const meterFillColor = "var(--dynasty-accent-fill, rgba(96,165,250,0.78))";
                          const openInKtcHref = showOpenInKtc
                            ? buildKtcTradeCalculatorUrl({
                                format: leagueSettings?.valueModeLabel === "1QB" ? "1QB" : "2QB",
                                leftIncomingAssets,
                                rightIncomingAssets,
                              })
                            : "";

                          return (
                            <div
                              key={`trade-comparison-${leftIncomingAssets.length}-${rightIncomingAssets.length}`}
                              style={{
                                padding: mobileTwoTeamTradeMode ? "14px 0 10px" : "18px 0 0",
                                border: "none",
                                background: mobileTwoTeamTradeMode ? "transparent" : "var(--dynasty-trade-panel-bg, rgba(20,28,46,0.98))",
                                borderRadius: 0,
                                display: "grid",
                                gap: 12,
                                minHeight: mobileTwoTeamTradeMode ? "100%" : undefined,
                                height: mobileTwoTeamTradeMode ? "100%" : undefined,
                                gridTemplateRows: mobileTwoTeamTradeMode ? "minmax(0, 1fr) auto" : undefined,
                                borderTop: "3px solid rgba(148,163,184,0.28)",
                                boxShadow: mobileTwoTeamTradeMode ? "inset 0 1px 0 rgba(255,255,255,0.04)" : "inset 0 1px 0 rgba(255,255,255,0.04), 0 -10px 30px rgba(0,0,0,0.28)",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                  gap: 10,
                                  alignItems: "start",
                                  minHeight: 0,
                                  overflowY: mobileTwoTeamTradeMode ? "auto" : undefined,
                                  overflowX: "hidden",
                                  alignContent: "start",
                                  paddingRight: mobileTwoTeamTradeMode ? 2 : 0,
                                  paddingTop: mobileTwoTeamTradeMode ? 10 : 0,
                                }}
                              >
                                {[
                                  {
                                    team: leftTeam,
                                    incomingAssets: leftIncomingAssets,
                                    displayReceiveValue: leftDisplayTotal,
                                    displayedValueAdjustment: leftTeam.rosterId === premiumTeamRosterId ? valueAdjustment : 0,
                                  },
                                  {
                                    team: rightTeam,
                                    incomingAssets: rightIncomingAssets,
                                    displayReceiveValue: rightDisplayTotal,
                                    displayedValueAdjustment: rightTeam.rosterId === premiumTeamRosterId ? valueAdjustment : 0,
                                  },
                                ].map(({ team, incomingAssets, displayReceiveValue, displayedValueAdjustment }) => (
                                  <div
                                    key={`comparison-${team.rosterId}`}
                                    style={{
                                      display: "grid",
                                      gap: 8,
                                      minWidth: 0,
                                      alignContent: "start",
                                      borderLeft: team.rosterId === rightTeam.rosterId ? "2px solid rgba(148,163,184,0.22)" : "none",
                                      paddingLeft: team.rosterId === rightTeam.rosterId ? 12 : 0,
                                    }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                      <TeamAvatar team={team} size={22} />
                                      <div
                                        style={{
                                          minWidth: 0,
                                          fontSize: 13,
                                          fontWeight: 900,
                                          color: "var(--text-0)",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {team.displayName}
                                      </div>
                                    </div>
                                    <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>
                                      Receive
                                    </div>
                                    {incomingAssets.length ? (
                                      incomingAssets.map((asset) => (
                                        <TradeAssetCard
                                          key={`comparison-receive-${team.rosterId}-${asset.sourceTeamId}-${asset.id}`}
                                          asset={asset}
                                          onClick={() => removeTradeAsset(team.rosterId, asset)}
                                          resolvedValue={resolveTradeAssetValue(asset)}
                                          pickBucket={pickValueAssignments[asset.id] ?? null}
                                          assignedRookie={pickRookieAssignments[asset.id] ?? null}
                                          onToggleRookiePicker={() =>
                                            setActiveRookiePickerId((prev) => (prev === asset.id ? null : asset.id))
                                          }
                                          onPickBucketChange={(bucket) => {
                                            setPickValueAssignments((prev) => ({ ...prev, [asset.id]: bucket }));
                                            setPickRookieAssignments((prev) => {
                                              const next = { ...prev };
                                              delete next[asset.id];
                                              return next;
                                            });
                                          }}
                                          currentLeagueSeason={leagueSeason}
                                          formatValue={formatValue}
                                          getPositionColor={getPositionColor}
                                        />
                                      ))
                                    ) : (
                                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.42)" }}>No players selected</div>
                                    )}
                                    <div style={{ minWidth: 0, display: "grid", gap: 2 }}>
                                      {!hideTradeCalculations && displayedValueAdjustment > 0 ? (
                                        <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.52)" }}>
                                          Value adjustment (+{formatValue(Math.round(displayedValueAdjustment))})
                                        </div>
                                      ) : (
                                        !hideTradeCalculations ? <div style={{ height: 14 }} /> : null
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {!hideTradeCalculations ? (
                                <div
                                style={{
                                  padding: "10px 14px 0",
                                  borderTop: "2px solid rgba(148,163,184,0.22)",
                                  display: "grid",
                                  gap: 10,
                                  alignSelf: "end",
                                }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                    gap: 10,
                                    alignItems: "end",
                                  }}
                                >
                                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.88)" }}>
                                    {formatValue(Math.round(leftDisplayTotal))}
                                  </div>
                                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.88)", textAlign: "right" }}>
                                    {formatValue(Math.round(rightDisplayTotal))}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    position: "relative",
                                    height: 28,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "absolute",
                                      left: 0,
                                      right: 0,
                                      height: 8,
                                      borderRadius: 999,
                                      overflow: "hidden",
                                      background: "rgba(255,255,255,0.08)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      marginLeft: twoTeamMeterCenterOffsetPx / 2,
                                      marginRight: -(twoTeamMeterCenterOffsetPx / 2),
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        left: `${meterFillLeft}%`,
                                        top: 0,
                                        bottom: 0,
                                        right: `${100 - meterFillRight}%`,
                                        background: meterFillColor,
                                        transition: "left 220ms ease, right 220ms ease",
                                      }}
                                    />
                                    <div
                                      style={{
                                        position: "absolute",
                                        left: "50%",
                                        top: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: 4,
                                        height: 18,
                                        borderRadius: 999,
                                        background: "rgba(255,255,255,0.32)",
                                        boxShadow: "0 0 0 1px rgba(7,10,18,0.38)",
                                      }}
                                    />
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 900,
                                    textAlign: "center",
                                    color: "var(--dynasty-accent, #60a5fa)",
                                    }}
                                  >
                                    {addPlayerValue > 0
                                    ? `${favoredTeamName} must add player worth ${formatValue(addPlayerValue)} for even trade.`
                                    : "Trade is even."}
                                  </div>
                                </div>
                              ) : null}
                              {showOpenInKtc ? <OpenInKtcButton href={openInKtcHref} /> : null}
                            </div>
                          );
                        })() : openTeams.map((team) => {
                          const incomingBySource = tradeSelections[team.rosterId] ?? {};
                          const incomingAssets = Object.values(incomingBySource).flat();
                          const outgoingAssets = Object.entries(tradeSelections)
                            .filter(([targetTeamId]) => Number(targetTeamId) !== team.rosterId)
                            .flatMap(([, incoming]) => incoming[team.rosterId] ?? []);
                          const {
                            actualReceiveValue,
                            actualSendValue,
                            adjustedReceiveValue,
                            adjustedSendValue,
                            receiveAdjustment,
                            sendAdjustment,
                            valueAdjustment,
                            favoredSide,
                            sideToEven,
                            addPlayerValue,
                          } = calculateKtcStyleAdjustedTotals(
                            incomingAssets,
                            outgoingAssets,
                            resolveTradeAssetValue,
                            overallMaxPlayerValue
                          );
                          const balanceDenominator = Math.max(1, adjustedReceiveValue + adjustedSendValue);
                          const balanceRatio = Math.max(-1, Math.min(1, (adjustedReceiveValue - adjustedSendValue) / balanceDenominator));
                          const balancePosition = 50 - balanceRatio * 50;
                          const meterFillLeft = Math.min(50, balancePosition);
                          const meterFillRight = Math.max(50, balancePosition);
                          const meterFillColor =
                            valueAdjustment > 0
                              ? favoredSide === "receive"
                                ? "var(--dynasty-positive-fill, rgba(74,222,128,0.72))"
                                : "var(--dynasty-negative-fill, rgba(248,113,113,0.72))"
                              : "rgba(255,255,255,0.2)";
                          const sideToEvenLabel =
                            sideToEven === "receive"
                              ? "receive"
                              : sideToEven === "send"
                                ? "send"
                                : null;
                          return (
                            <div
                              key={`trade-${team.rosterId}`}
                              style={{
                                padding: "12px 14px",
                                border: "1px solid rgba(255,255,255,0.08)",
                                background: "rgba(255,255,255,0.04)",
                                borderRadius: 14,
                                display: "grid",
                                gap: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 10,
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <TeamAvatar team={team} size={22} />
                                  <div
                                    style={{
                                      minWidth: 0,
                                      fontSize: 13,
                                      fontWeight: 900,
                                      color: "var(--text-0)",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {team.displayName}
                                  </div>
                                </div>
                                {openTeams.length >= 3 ? (
                                  <button
                                    type="button"
                                    onClick={() => setSelectionMode({ teamId: team.rosterId })}
                                    style={{
                                      padding: "6px 9px",
                                      borderRadius: 10,
                                      border: "1px solid rgba(255,255,255,0.12)",
                                      background:
                                        selectionMode?.teamId === team.rosterId
                                          ? "rgba(255,255,255,0.16)"
                                          : "rgba(255,255,255,0.06)",
                                      color: "var(--text-0)",
                                      fontSize: 10,
                                      fontWeight: 800,
                                      cursor: "pointer",
                                      flex: "0 0 auto",
                                    }}
                                  >
                                    Receive
                                  </button>
                                ) : null}
                              </div>

                              <div style={{ display: "grid", gap: 8 }}>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                    gap: 10,
                                    alignItems: "start",
                                  }}
                                >
                                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                                    <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>
                                      Receive
                                    </div>
                                    {incomingAssets.length ? (
                                      incomingAssets
                                        .map((asset) => (
                                          <TradeAssetCard
                                            key={`receive-${team.rosterId}-${asset.sourceTeamId}-${asset.id}`}
                                            asset={asset}
                                            onClick={() => removeTradeAsset(team.rosterId, asset)}
                                            resolvedValue={resolveTradeAssetValue(asset)}
                                            pickBucket={pickValueAssignments[asset.id] ?? null}
                                            assignedRookie={pickRookieAssignments[asset.id] ?? null}
                                            onToggleRookiePicker={() =>
                                              setActiveRookiePickerId((prev) => (prev === asset.id ? null : asset.id))
                                            }
                                            onPickBucketChange={(bucket) => {
                                              setPickValueAssignments((prev) => ({ ...prev, [asset.id]: bucket }));
                                              setPickRookieAssignments((prev) => {
                                                const next = { ...prev };
                                                delete next[asset.id];
                                                return next;
                                              });
                                            }}
                                            currentLeagueSeason={leagueSeason}
                                            formatValue={formatValue}
                                            getPositionColor={getPositionColor}
                                          />
                                        ))
                                    ) : (
                                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.42)" }}>No players selected</div>
                                    )}
                                  </div>

                                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                                    <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>
                                      Send
                                    </div>
                                    {outgoingAssets.length ? (
                                      outgoingAssets.map((asset) => (
                                        <TradeAssetCard
                                          key={`send-${team.rosterId}-${asset.sourceTeamId}-${asset.id}`}
                                          asset={asset}
                                          onClick={() => removeTradeAsset(team.rosterId, asset)}
                                          resolvedValue={resolveTradeAssetValue(asset)}
                                          pickBucket={pickValueAssignments[asset.id] ?? null}
                                          assignedRookie={pickRookieAssignments[asset.id] ?? null}
                                          onToggleRookiePicker={() =>
                                            setActiveRookiePickerId((prev) => (prev === asset.id ? null : asset.id))
                                          }
                                          onPickBucketChange={(bucket) => {
                                            setPickValueAssignments((prev) => ({ ...prev, [asset.id]: bucket }));
                                            setPickRookieAssignments((prev) => {
                                              const next = { ...prev };
                                              delete next[asset.id];
                                              return next;
                                            });
                                          }}
                                          currentLeagueSeason={leagueSeason}
                                          formatValue={formatValue}
                                          getPositionColor={getPositionColor}
                                        />
                                      ))
                                    ) : (
                                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.42)" }}>No players selected</div>
                                    )}
                                  </div>
                                </div>

                                {!hideTradeCalculations ? <div
                                  style={{
                                    paddingTop: 6,
                                    borderTop: "1px solid rgba(255,255,255,0.08)",
                                    display: "grid",
                                    gap: 8,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                      gap: 10,
                                      alignItems: "start",
                                    }}
                                  >
                                    <div style={{ minWidth: 0, display: "grid", gap: 2 }}>
                                      {receiveAdjustment > 0 ? (
                                        <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.52)" }}>
                                          Value adjustment (+{formatValue(Math.round(receiveAdjustment))})
                                        </div>
                                      ) : (
                                        <div style={{ height: 14 }} />
                                      )}
                                      <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.88)" }}>
                                        {formatValue(Math.round(actualReceiveValue))}
                                      </div>
                                    </div>
                                    <div style={{ minWidth: 0, display: "grid", gap: 2, textAlign: "right" }}>
                                      {sendAdjustment > 0 ? (
                                        <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.52)" }}>
                                          Value adjustment (+{formatValue(Math.round(sendAdjustment))})
                                        </div>
                                      ) : (
                                        <div style={{ height: 14 }} />
                                      )}
                                      <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.88)" }}>
                                        {formatValue(Math.round(actualSendValue))}
                                      </div>
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      position: "relative",
                                      height: 26,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        height: 8,
                                        borderRadius: 999,
                                        overflow: "hidden",
                                        background: "rgba(255,255,255,0.08)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: `${meterFillLeft}%`,
                                          top: 0,
                                          bottom: 0,
                                          right: `${100 - meterFillRight}%`,
                                          background: meterFillColor,
                                          transition: "left 220ms ease, right 220ms ease, background 220ms ease",
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 900,
                                      textAlign: "center",
                                      color:
                                        valueAdjustment > 0
                                          ? favoredSide === "receive"
                                            ? "#4ade80"
                                            : "#f87171"
                                          : "rgba(255,255,255,0.72)",
                                    }}
                                  >
                                    {addPlayerValue > 0 && sideToEvenLabel
                                      ? `Add player worth ${formatValue(Math.round(addPlayerValue))} to ${sideToEvenLabel} to even`
                                      : "Trade is even."}
                                  </div>
                                </div> : null}
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                      )}
                    </aside>
                  </div>
                ) : (
                  !isNarrowViewport ? (
                    <div
                      style={{
                        minHeight: 220,
                        border: "1px dashed rgba(255,255,255,0.12)",
                        borderRadius: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.5)",
                        fontSize: 13,
                        fontWeight: 700,
                        background: "rgba(255,255,255,0.02)",
                      }}
                    >
                      Click a team name to open its roster. Click multiple teams to add more columns.
                    </div>
                  ) : null
                )}
              </div>
            </div>
            {activeRookiePickerId ? (
              <div
                onMouseDown={() => setActiveRookiePickerId(null)}
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(5,7,15,0.72)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 40,
                  padding: 20,
                }}
              >
                <div
                  onMouseDown={(event) => event.stopPropagation()}
                  style={{
                    width: "min(560px, 92vw)",
                    maxHeight: "min(680px, 84vh)",
                    borderRadius: 18,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(15,19,32,0.98)",
                    boxShadow: "0 24px 80px rgba(0,0,0,0.42)",
                    display: "grid",
                    gridTemplateRows: "auto minmax(0, 1fr)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "grid", gap: 2 }}>
                      <div style={{ fontSize: 15, fontWeight: 900, color: "var(--text-0)" }}>Select Rookie Value</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.58)" }}>
                        Rookies sorted highest to lowest by {leagueSettings?.valueModeLabel ?? "KTC"} value.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveRookiePickerId(null)}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.06)",
                        color: "var(--text-0)",
                        fontSize: 14,
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    className="dynastyRosterScroll"
                    style={{
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: 12,
                      display: "grid",
                      gap: 6,
                    }}
                  >
                    {rookiePickOptions.map((rookie) => (
                      <button
                        key={`rookie-picker-${rookie.id}`}
                        type="button"
                        onClick={() => {
                          setPickRookieAssignments((prev) => ({ ...prev, [activeRookiePickerId]: rookie }));
                          setActiveRookiePickerId(null);
                        }}
                        style={{
                          border: "1px solid rgba(255,255,255,0.08)",
                          background: pickRookieAssignments[activeRookiePickerId]?.id === rookie.id ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.04)",
                          color: "var(--text-0)",
                          borderRadius: 10,
                          padding: "10px 12px",
                          cursor: "pointer",
                          textAlign: "left",
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {rookie.name}
                          </div>
                          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.52)", marginTop: 3 }}>
                            <span style={{ color: getPositionColor(rookie.position), fontWeight: 800 }}>{rookie.position}</span>
                            {rookie.team ? <span>{` • ${rookie.team}`}</span> : null}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.84)" }}>
                          {formatValue(rookie.value)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </ImportedTeamsLayout>
        ) : null}
      </div>
      {mobileTradeMeterOverlay}
    </div>
  );
}
